import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Alert, Badge, Box, Divider, Typography, Stack, MenuItem, Tooltip } from '@mui/material';
// routes
import { PATH_DASHBOARD, PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import usePermissions from '../../../hooks/usePermissions';
import { hasPermission } from '../../../constants/permissions';
// redux
import { useSelector } from '../../../redux/store';
// utils
import { resolvePlanTier, getVenueLimit, TIER_LABELS } from '../../../utils/planGating';
// components
import Image from '../../../components/Image';
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
// translations
import { tokens } from '../../../locales/tokens';
import { WarningIcon } from 'src/theme/overrides/CustomIcons';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  // {
  //   label: 'Home',
  //   linkTo: '/',
  // },
  // {
  //   label: 'Profile',
  //   linkTo: PATH_DASHBOARD.user.profile,
  // },
  {
    label: (tokens.nav.account),
    linkTo: PATH_DASHBOARD.user.account,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const { user, logout } = useAuth();
  const { loginPermissions } = usePermissions();
  const canManageBilling = hasPermission(loginPermissions, 'billing.manage');

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const { t, allLangs, currentLang, onChangeLang } = useLocales();

  const { venues } = useSelector((state) => state.venue);

  const planTier = resolvePlanTier(user?.plan_tier);
  const venueLimit = getVenueLimit(planTier);
  const activeVenuesCount = venues.filter((v) => v.status).length;
  const overLimit = canManageBilling && venueLimit !== null && activeVenuesCount > venueLimit;

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleTour = async () => {
    localStorage.setItem('joyrideSeen', '');
    setOpen(null);
    navigate(PATH_DASHBOARD.general.app) 
  }

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          variant="dot"
          color="warning"
          invisible={!overLimit}
        >
          <MyAvatar />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          width: 240,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
          {user?.organization_name && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }} noWrap>
              {user.organization_name}
            </Typography>
          )}
        </Box>

        {canManageBilling && (
          <>
            <Divider sx={{ borderStyle: 'dashed' }} />

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t(tokens.tenantContext.currentPlan)}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.75,
                  typography: 'caption',
                  fontWeight: 700,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                  color: 'primary.main',
                }}
              >
                {TIER_LABELS[planTier]}
              </Box>
            </Stack>
            <Box sx={{ px: 2.5, pb: 1.5, mt: -1 }}>
              {overLimit ? (
                <Tooltip title={t(tokens.pages.venues.overLimitTooltip)} placement="top" arrow>
                  <Alert
                    severity="warning"
                    onClick={() => {
                      navigate(PATH_DASHBOARD.organization.subscription);
                      handleClose();
                    }}
                    sx={{
                      py: 0,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.85 },
                      '& .MuiAlert-message': { typography: 'caption' },
                    }}
                    icon={<WarningIcon sx={{ height: 18, width: 18 }} />}
                  >
                    {t(tokens.tenantContext.venueLimitUsage, { count: activeVenuesCount, limit: venueLimit })}
                  </Alert>
                </Tooltip>
              ) : (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {venueLimit === null
                    ? t(tokens.tenantContext.venueLimitUsageUnlimited, { count: activeVenuesCount })
                    : t(tokens.tenantContext.venueLimitUsage, { count: activeVenuesCount, limit: venueLimit })}
                </Typography>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
            >
              {t(option.label)}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
            <MenuItem
              onClick={handleTour}
            >
              {t(tokens.joyride.tour)}
            </MenuItem>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {allLangs.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === currentLang.value}
              onClick={() => {
                onChangeLang(option.value);
                handleClose();
              }}
            >
              <Image
                disabledEffect
                alt={option.label}
                src={option.icon}
                sx={{ width: 28, mr: 2 }}
              />
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          {t(tokens.nav.logout)}
        </MenuItem>
      </MenuPopover>
    </>
  );
}
